import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge, Button, Card, CardContent, Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle, Input, Label,
} from '@werkstatt/ui';
import { Check, Copy, ShieldCheck, ShieldOff } from 'lucide-react';
import api from '../../lib/api';
import { readApiError } from '../../lib/apiError';
import type { TwoFactorSetup, TwoFactorStatus } from '../../types/twofactor';

function BackupCodesList({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    await navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-4 font-mono text-sm">
        {codes.map((code) => (
          <div key={code}>{code}</div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={copyAll}>
        {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
        {copied ? 'Kopiert' : 'Alle Codes kopieren'}
      </Button>
      <p className="text-xs text-muted-foreground">
        Bewahren Sie diese Codes sicher auf. Jeder Code funktioniert nur einmal
        und ersetzt Ihre Authenticator-App, falls Sie keinen Zugriff darauf haben.
      </p>
    </div>
  );
}

export function TwoFactorTab() {
  const queryClient = useQueryClient();
  const invalidateStatus = () => queryClient.invalidateQueries({ queryKey: ['twofactor-status'] });

  const { data: status } = useQuery({
    queryKey: ['twofactor-status'],
    queryFn: async () => (await api.get<TwoFactorStatus>('/twofactor/status')).data,
  });

  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
  const [step, setStep] = useState<'idle' | 'confirm' | 'backup-codes'>('idle');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableError, setDisableError] = useState('');

  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);

  const setupMutation = useMutation({
    mutationFn: async () => (await api.post<TwoFactorSetup>('/twofactor/setup')).data,
    onSuccess: (data) => {
      setSetupData(data);
      setStep('confirm');
    },
    onError: (error: unknown) => toast.error(readApiError(error, 'Einrichtung fehlgeschlagen')),
  });

  const verifyMutation = useMutation({
    mutationFn: async () => (await api.post('/twofactor/verify', { code })).data,
    onSuccess: () => {
      setCodeError('');
      setStep('backup-codes');
    },
    onError: (error: unknown) => setCodeError(readApiError(error, 'Ungültiger Code')),
  });

  const disableMutation = useMutation({
    mutationFn: async () =>
      (await api.post('/twofactor/disable', { current_password: disablePassword })).data,
    onSuccess: () => {
      toast.success('2FA deaktiviert');
      setDisableOpen(false);
      setDisablePassword('');
      setDisableError('');
      invalidateStatus();
    },
    onError: (error: unknown) => setDisableError(readApiError(error, 'Deaktivieren fehlgeschlagen')),
  });

  const regenerateMutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ backup_codes: string[] }>('/twofactor/backup-codes/regenerate')).data,
    onSuccess: (data) => {
      setNewBackupCodes(data.backup_codes);
      toast.success('Neue Backup-Codes erzeugt');
    },
    onError: (error: unknown) => toast.error(readApiError(error, 'Erzeugen fehlgeschlagen')),
  });

  const finishSetup = () => {
    setSetupData(null);
    setStep('idle');
    setCode('');
    invalidateStatus();
  };

  if (step === 'confirm' && setupData) {
    return (
      <Card variant="glass">
        <CardContent className="p-6 space-y-6 max-w-md">
          <div>
            <h3 className="font-medium mb-2">1. QR-Code scannen</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Scannen Sie den Code mit einer Authenticator-App (z. B. Google
              Authenticator, Authy) oder tragen Sie das Secret manuell ein.
            </p>
            <img
              src={setupData.qr_code_data_uri}
              alt="QR-Code für die Authenticator-App"
              className="rounded-md border"
              width={200}
              height={200}
            />
            <p className="mt-2 font-mono text-xs break-all text-muted-foreground">
              {setupData.secret}
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">2. Code bestätigen</h3>
            <Label htmlFor="twofactor-code">6-stelliger Code aus der App</Label>
            <Input
              id="twofactor-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              autoFocus
            />
            {codeError && <p className="text-sm text-destructive">{codeError}</p>}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setSetupData(null); setStep('idle'); }}>
              Abbrechen
            </Button>
            <Button
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending || code.length < 6}
            >
              {verifyMutation.isPending ? 'Wird geprüft…' : 'Bestätigen'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'backup-codes' && setupData) {
    return (
      <Card variant="glass">
        <CardContent className="p-6 space-y-4 max-w-md">
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
            <h3 className="font-medium">2FA aktiviert</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Speichern Sie diese Backup-Codes an einem sicheren Ort — sie helfen
            Ihnen beim Anmelden, falls Sie keinen Zugriff auf Ihre
            Authenticator-App haben.
          </p>
          <BackupCodesList codes={setupData.backup_codes} />
          <Button onClick={finishSetup}>Fertig</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="glass">
      <CardContent className="p-6 space-y-4 max-w-md">
        <div className="flex items-center gap-3">
          {status?.enabled ? (
            <Badge variant="outline" className="gap-1 text-primary border-primary/40">
              <ShieldCheck className="h-3.5 w-3.5" /> Aktiv
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <ShieldOff className="h-3.5 w-3.5" /> Nicht aktiviert
            </Badge>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          Mit Zwei-Faktor-Authentifizierung brauchen Sie beim Anmelden neben
          Ihrem Passwort zusätzlich einen Code aus einer Authenticator-App.
        </p>

        {status?.enabled ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              Neue Backup-Codes erzeugen
            </Button>
            <Button variant="destructive" onClick={() => setDisableOpen(true)}>
              Deaktivieren
            </Button>
          </div>
        ) : (
          <Button onClick={() => setupMutation.mutate()} disabled={setupMutation.isPending}>
            {setupMutation.isPending ? 'Wird eingerichtet…' : 'Aktivieren'}
          </Button>
        )}

        {newBackupCodes && (
          <div className="pt-2 border-t space-y-2">
            <p className="text-sm font-medium">Neue Backup-Codes</p>
            <BackupCodesList codes={newBackupCodes} />
          </div>
        )}
      </CardContent>

      <Dialog open={disableOpen} onOpenChange={(next) => !next && setDisableOpen(false)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-display">2FA deaktivieren?</DialogTitle>
            <DialogDescription>
              Bitte bestätigen Sie mit Ihrem aktuellen Passwort.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="disable-password">Aktuelles Passwort</Label>
            <Input
              id="disable-password"
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              autoFocus
            />
            {disableError && <p className="text-sm text-destructive">{disableError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDisableOpen(false); setDisablePassword(''); setDisableError(''); }}
              disabled={disableMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => disableMutation.mutate()}
              disabled={disableMutation.isPending || !disablePassword}
            >
              {disableMutation.isPending ? 'Wird deaktiviert…' : 'Deaktivieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
